import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Accordion, AccordionDetails, AccordionSummary, Link, Stack, Typography } from "@mui/material";
import { ReactComponent as ArrowUpIcon } from "Components/_Shared/Icons/Assets/arrow-up.svg";
import { ClickableImage } from "Components/_Shared/ImageLightbox/ClickableImage";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { ReactNode } from "react";
import { useState } from "react";

type TranslateFn = ReturnType<typeof useResource>["t"];

type FAQImage = {
  src: string;
  alt: string;
};

type FAQItem = {
  question: string;
  answer: string;
  image?: FAQImage;
};

type FAQLink = {
  label: string;
  href: string;
};

type FAQSectionData = {
  title: string;
  items: (FAQItem | FAQLink)[];
};

const FINANCIAL_PLANNING_URL = "https://upka.zfpa.cz/prezentace/2/2/FinancniPlanovani";

const buildFaqSections = (t: TranslateFn): FAQSectionData[] => [
  {
    title: t(Resources.ReportIssue.FAQ.Sections.General.Title),
    items: [
      {
        question: t(Resources.ReportIssue.FAQ.Sections.General.Item1.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.General.Item1.Answer),
      },
    ],
  },
  {
    title: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Title),
    items: [
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item1.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item1.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item2.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item2.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item3.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item3.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item4.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item4.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item5.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item5.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item6.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item6.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item7.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.ClientAndFamily.Item7.Answer),
      },
    ],
  },
  {
    title: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Title),
    items: [
      {
        question: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item1.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item1.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item2.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item2.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item3.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item3.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item4.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item4.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item5.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.GoalsAndQuestionnaire.Item5.Answer),
      },
    ],
  },
  {
    title: t(Resources.ReportIssue.FAQ.Sections.Contracting.Title),
    items: [
      {
        question: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item1.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item1.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item2.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item2.Answer),
        image: {
          src: "https://cdn.zfpa.cz/faq/vlastni_reseni.jpg",
          alt: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item2.ImageAlt),
        },
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item3.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item3.Answer),
        image: {
          src: "https://cdn.zfpa.cz/faq/oblibena_portfolia.png",
          alt: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item3.ImageAlt),
        },
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item4.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item4.Answer),
      },
      {
        question: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item5.Question),
        answer: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item5.Answer),
        image: {
          src: "https://cdn.zfpa.cz/faq/uprava_navrhovane_reseni.png",
          alt: t(Resources.ReportIssue.FAQ.Sections.Contracting.Item5.ImageAlt),
        },
      },
    ],
  },
  {
    title: t(Resources.ReportIssue.FAQ.Sections.SupportingMaterials.Title),
    items: [
      {
        label: t(Resources.ReportIssue.FAQ.Sections.SupportingMaterials.FinancialPlanning),
        href: FINANCIAL_PLANNING_URL,
      },
    ],
  },
];

type FAQAccordionItemProps = FAQItem & {
  expanded: boolean;
  onChange: (expanded: boolean) => void;
};

const renderWithBold = (text: string): ReactNode[] => {
  const parts = text.split(/(\*\*.+?\*\*)/g);

  return parts.map((part, index) => {
    const boldMatch = part.match(/^\*\*(.+)\*\*$/);
    if (!boldMatch) {
      return part;
    }

    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: parts are derived from a static, non-reorderable string
      <Typography key={index} component="span" fontWeight={700} fontSize="inherit" color="inherit">
        {boldMatch[1]}
      </Typography>
    );
  });
};

const FAQAccordionItem = ({ question, answer, image, expanded, onChange }: FAQAccordionItemProps) => (
  <Accordion
    expanded={expanded}
    onChange={(_event, isExpanded) => onChange(isExpanded)}
    sx={(theme) => ({
      padding: `${theme.spacing(Spacing.L)} ${theme.spacing(Spacing.XL)}`,
      border: "1px solid",
      borderColor: expanded ? "primary.main" : theme.colors.borderLight,
      borderRadius: `${theme.spacing(Radius.M)} !important`,
      boxShadow: `${theme.shadows[0]} !important`,
      "&::before": {
        display: "none",
      },
      "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content .Mui-expanded": {
        margin: "0 !important",
      },
      "& .MuiAccordion-heading > .MuiButtonBase-root": {
        minHeight: 0,
        padding: 0,
      },
      "& .MuiButtonBase-root.Mui-expanded": {
        minHeight: 0,
      },
      "& .MuiAccordionDetails-root": {
        padding: `${theme.spacing(Spacing.M)} 0 0`,
      },
    })}
  >
    <AccordionSummary expandIcon={<ArrowUpIcon style={{ transform: "rotate(180deg)" }} />}>
      <Typography variant="subtitle1" fontWeight={500} fontSize={14} lineHeight={1.4}>
        {question}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={Spacing.M}>
        <Typography variant="body2" fontSize={14} sx={{ color: "text.secondary", whiteSpace: "pre-line" }}>
          {renderWithBold(answer)}
        </Typography>
        {image && <ClickableImage src={image.src} alt={image.alt} />}
      </Stack>
    </AccordionDetails>
  </Accordion>
);

const FAQLinkItem = ({ label, href }: FAQLink) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    underline="none"
    color="text.primary"
    sx={(theme) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: Spacing.M,
      padding: `${theme.spacing(Spacing.L)} ${theme.spacing(Spacing.XL)}`,
      border: "1px solid",
      borderColor: theme.colors.borderLight,
      borderRadius: theme.spacing(Radius.M),
      "&:hover": {
        borderColor: "primary.main",
      },
    })}
  >
    <Typography variant="subtitle1" fontWeight={500} fontSize={14} lineHeight={1.4} color="inherit">
      {label}
    </Typography>
    <OpenInNewIcon sx={{ fontSize: 18, color: "text.secondary" }} />
  </Link>
);

const FAQSectionBlock = ({ title, items }: FAQSectionData) => {
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);

  return (
    <Stack spacing={Spacing.M} pl={Spacing.XL}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Stack spacing={Spacing.S}>
        {items.map((item) =>
          "href" in item ? (
            <FAQLinkItem key={item.href} {...item} />
          ) : (
            <FAQAccordionItem
              key={item.question}
              {...item}
              expanded={expandedQuestion === item.question}
              onChange={(isExpanded) => setExpandedQuestion(isExpanded ? item.question : null)}
            />
          ),
        )}
      </Stack>
    </Stack>
  );
};

export const ReportIssueFAQ = () => {
  const { t } = useResource();
  const sections = buildFaqSections(t);

  return (
    <Stack spacing={Spacing.XXL} width="70%">
      {sections.map((section) => (
        <FAQSectionBlock key={section.title} {...section} />
      ))}
    </Stack>
  );
};
